import { useEffect } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router';

import { AdminLoginForm } from '@/components/admin/admin-login-form';
import { LanguageSwitch } from '@/components/admin/language-switch';
import { KadirMark } from '@/components/kadir-mark';
import { useAdminSession } from '@/app/admin-session';
import { useTranslations } from '@/i18n/use-locale';
import { ADMIN_HOME_PATH, safeReturnTo } from '@/lib/admin-access';
import { useLogin } from '@/app/use-login';
import { Card } from '@/components/ui/card';

/**
 * The login page, and the only route outside the authenticated shell.
 *
 * `next` is sanitized before it is followed — `safeReturnTo` is unchanged from
 * the Next.js console and still earns its place: without it
 * `?next=https://evil.example` turns this page into one hop of a phishing flow,
 * which is the classic open redirect and a login page is where it always lands.
 */
export function LoginRoute() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { state: session } = useAdminSession();
  const { state, pending, submitPassword, submitCode } = useLogin();
  // The brand wordmark lives in its own namespace (the sidebar reads it too),
  // not under admin.login — reading it from there rendered the raw key.
  const brand = useTranslations('brand');

  const next = safeReturnTo(params.get('next')) ?? ADMIN_HOME_PATH;

  // Signing in flips the session, and this is what acts on it — rather than the
  // hook navigating, which would put routing inside something a test can drive
  // with no router at all.
  useEffect(() => {
    if (session.status === 'signedIn') void navigate(next, { replace: true });
  }, [session.status, navigate, next]);

  // An admin who is already signed in and types the login URL gets the console,
  // not a form asking them to do it again.
  if (session.status === 'signedIn') {
    return <Navigate to={next} replace />;
  }

  return (
    <main className="relative flex min-h-screen items-center justify-center bg-conc-50 p-4">
      {/* Every sign-in step renders here — password, code, first enrolment — so
          one switch covers all of them. */}
      <LanguageSwitch className="absolute end-4 top-4" />

      <div className="w-full max-w-[400px] space-y-6">
        <div className="flex items-center justify-center gap-2">
          <KadirMark className="h-9 w-9" />
          <span className="text-[15px] font-bold text-navy">{brand('adminWordmark')}</span>
        </div>

        <Card className="p-6 shadow-sm">
          <AdminLoginForm
            state={state}
            pending={pending}
            onPassword={(email, password) => void submitPassword(email, password)}
            onCode={(code) => void submitCode(code)}
          />
        </Card>
      </div>
    </main>
  );
}
